import type { CostSectionView } from "@/util/cost-section-view"
import { formatSnapshotMonth } from "@/util/describe-trend"

// Pure model for the "{Location} Pet Transport at a Glance" block on the pet
// city and state landing pages (MAR-78). Each stat has one fixed source, chosen
// so the block cannot disagree with the rest of the page:
//
//   - cost stats come from the same buildCostSectionView inputs and selection
//     logic as PetCostSection, so the typical rate, range and trend are the band
//     cards and trend summary restated — not a second cost computation.
//   - the data window is the snapshot's dataWindow, the same value the band's
//     "from bookings completed between …" disclosure cites. It moves with the
//     monthly snapshot refresh; nothing here is a hardcoded date. It captions
//     the block (buildPetGlanceTimeframe) rather than occupying a cell: it is
//     not a marketplace metric, and it qualifies the cost and distance stats
//     beside it.
//   - the trip distance (MAR-82) is the one figure with no counterpart further
//     down. buildCostSectionView still selects it — the selected band's own
//     mileage when usable, the marketplace's otherwise — so it can come from a
//     different scope than the rate beside it, and its detail line names that
//     scope itself.
//   - the shipment count is the committed marketplace total already shown for
//     every OTHER city in the page's destination link block.
//
// A metric without enough data behind it is dropped rather than padded (MAR-78:
// "Do not display weak or misleading statistics purely for template
// consistency"), so callers must render whatever list comes back, not a fixed
// set of slots.

export type GlanceStat = {
  // Stable identity for React keys and tests; never rendered.
  key: string
  // Self-contained enough to read on its own, outside the visual grid — the
  // block is a retrieval target for search/AI as much as a UI element.
  label: string
  value: string
  // One short sentence naming the scope or qualifying the value. Null when the
  // value needs no qualification.
  detail: string | null
}

// Below this the count reads as evidence of a thin market rather than of
// marketplace depth, which is the opposite of what the block is for. Those
// cities keep every other stat; they just don't headline a two-digit total.
export const MIN_SHIPMENTS_POSTED = 50

// A "glance" needs enough rows to be worth its own heading. In practice the
// marketplace fallback guarantees a band, so this only fires on a degenerate
// snapshot — in which case the page is better off going straight to its content.
export const MIN_GLANCE_STATS = 3

type PetGlanceArgs = {
  // Resolved once per page and shared with PetCostSection.
  costView: CostSectionView
  // Committed marketplace total for this location, or null when the dataset has
  // no entry for it.
  shipmentsPosted: number | null
  // Fallback scope wording for a location with no local band ("Phoenix, AZ").
  locationLabel: string
}

// The committed shipment datasets store their totals as display strings
// ("3,328"), so a bare Number() is NaN. Returns null for a missing, malformed or
// non-positive entry, which the caller passes straight through as "no stat".
export function parseCommittedCount(
  value: string | number | null | undefined,
): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null
  }

  if (typeof value !== "string") {
    return null
  }

  const parsed = Number(value.replace(/,/g, "").trim())

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function dollars(value: number): string {
  return `$${value.toFixed(2)}`
}

function perMile(value: number): string {
  return `${dollars(value)} per mile`
}

// Feed mileages are volume-weighted aggregates, so they can carry a fractional
// tail that means nothing to a reader; a trip distance is only meaningful to the
// mile. Locale is pinned — an unqualified toLocaleString renders "1.635" on a
// German-locale browser against the server's "1,635" and trips hydration
// (MAR-81).
function mileCount(value: number): string {
  return Math.round(value).toLocaleString("en-US")
}

function miles(value: number): string {
  return `${mileCount(value)} ${Math.round(value) === 1 ? "mile" : "miles"}`
}

// "Across CitizenShipper routes touching Phoenix, AZ" / the marketplace-wide
// equivalent. Mirrors how the cost section labels the same numbers: a stat is
// only allowed to name a location when the figure behind it is actually that
// location's.
function scopeLine(isLocal: boolean, scopeLabel: string | null): string {
  return isLocal && scopeLabel
    ? `Across CitizenShipper marketplace routes touching ${scopeLabel}.`
    : "Across pet transports on the entire CitizenShipper marketplace."
}

// Compact restatement of the trend the chart below plots. Uses the same
// Math.abs(percentChange) and first-quarter label as describeTrend, so the two
// can't quote different numbers for the same movement.
function trendValue(trend: CostSectionView["trend"]): string | null {
  if (!trend) {
    return null
  }

  const since = trend.data.quarters[0].label

  if (trend.data.direction === "flat") {
    return `Roughly steady since ${since}`
  }

  if (!Number.isFinite(trend.data.percentChange)) {
    return null
  }

  const word = trend.data.direction === "up" ? "Up" : "Down"

  return `${word} about ${Math.abs(trend.data.percentChange)}% since ${since}`
}

// The one window every feed-backed figure in this block shares, stated once
// under the heading instead of repeated in each cell it qualifies.
//
// It names its scope deliberately, and only for figures the block actually
// shows: "cost and distance" when both render, one of them when the other was
// omitted. Two stats fall OUTSIDE it and must not be read as covered —
//   - "Pet shipments posted" comes from the committed stateCityShipments /
//     stateShipments datasets, which carry a bare count and no window at all.
//   - the trend names its own first quarter in its value ("since 2025 Q3"),
//     which can be shorter than the snapshot window for a local scope.
// Widening this sentence to "all figures" would make a claim the data does not
// support for either one.
export function buildPetGlanceTimeframe(
  costDataWindow: { from: string; to: string } | null,
  { band, mileage }: Pick<CostSectionView, "band" | "mileage">,
): string | null {
  const subject = timeframeSubject(Boolean(band), Boolean(mileage))

  if (!costDataWindow || !subject) {
    return null
  }

  return `${subject} reflect bookings completed between ${formatSnapshotMonth(
    costDataWindow.from,
  )} and ${formatSnapshotMonth(costDataWindow.to)}.`
}

function timeframeSubject(
  hasCost: boolean,
  hasDistance: boolean,
): string | null {
  if (hasCost && hasDistance) {
    return "Cost and distance figures"
  }

  if (hasCost) {
    return "Cost figures"
  }

  if (hasDistance) {
    return "Distance figures"
  }

  return null
}

export function buildPetGlanceStats({
  costView,
  shipmentsPosted,
  locationLabel,
}: PetGlanceArgs): GlanceStat[] {
  const stats: GlanceStat[] = []
  const { band, mileage, trend, scopeLabel } = costView

  if (band) {
    const scope = scopeLine(band.isLocal, scopeLabel ?? locationLabel)

    stats.push({
      key: "typical-cost",
      label: "Typical pet transport cost",
      value: perMile(band.percentiles.typical),
      detail: scope,
    })
    stats.push({
      key: "cost-range",
      label: "Usual cost range",
      value: `${dollars(band.percentiles.low)}–${perMile(
        band.percentiles.high,
      )}`,
      detail: `${scope} Peak rates reach ${perMile(band.percentiles.peak)}.`,
    })
  }

  // MAR-82. The p25-p75 span is a detail rather than its own stat: it qualifies
  // the median the same way the peak rate qualifies the cost range, and the
  // block stays a glance. Dropped entirely when the two ends round together, so
  // a thin scope never prints "Usual range 300-300 miles".
  if (mileage) {
    const scope = scopeLine(mileage.isLocal, scopeLabel ?? locationLabel)
    const { low, typical, high } = mileage.percentiles
    const hasSpan = mileCount(low) !== mileCount(high)

    stats.push({
      key: "trip-distance",
      label: "Median trip distance",
      value: miles(typical),
      detail: hasSpan
        ? `${scope} Usual range ${mileCount(low)}–${miles(high)}.`
        : scope,
    })
  }

  if (
    shipmentsPosted !== null &&
    Number.isFinite(shipmentsPosted) &&
    shipmentsPosted >= MIN_SHIPMENTS_POSTED
  ) {
    stats.push({
      key: "shipments-posted",
      label: "Pet shipments posted",
      value: shipmentsPosted.toLocaleString("en-US"),
      detail: `Listings involving ${locationLabel} on the CitizenShipper marketplace.`,
    })
  }

  const movement = trendValue(trend)

  if (trend && movement) {
    stats.push({
      key: "cost-trend",
      label: "Recent cost-per-mile trend",
      value: movement,
      detail: scopeLine(trend.isLocal, scopeLabel ?? locationLabel),
    })
  }

  return stats.length >= MIN_GLANCE_STATS ? stats : []
}
